import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { todayISO, ddmmyyyy, when } from "@/lib/format";
import { ACTION_STYLE, ChangeList } from "@/lib/history";

const dayBounds = (d) => {
  const a = new Date(`${d}T00:00:00`);
  const b = new Date(a);
  b.setDate(b.getDate() + 1);
  return { start: a.toISOString(), end: b.toISOString() };
};

const Count = ({ n, l, tone }) => (
  <span className={`text-[11px] font-bold px-2 py-0.5 ${n ? tone : "bg-slate-100 text-slate-400"}`}>{n} {l}</span>
);

const Person = ({ p, nav }) => (
  <section className="bg-white border border-slate-300 fade-up" data-testid={`activity-person-${p.by_id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b-2 border-slate-900">
      <h2 className="font-extrabold text-base md:text-lg tracking-tight">{p.by_name}</h2>
      <div className="flex flex-wrap gap-1.5">
        <Count n={p.created} l="added" tone={ACTION_STYLE.created} />
        <Count n={p.updated} l="edited" tone={ACTION_STYLE.updated} />
        <Count n={p.deleted} l="deleted" tone={ACTION_STYLE.deleted} />
        <Count n={p.imported} l="imported" tone={ACTION_STYLE.imported} />
      </div>
    </div>
    <ul className="divide-y divide-slate-200">
      {p.items.map((h, i) => (
        <li key={i} className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors" data-testid="activity-item"
          onClick={() => h.action !== "deleted" && nav(`/book/${h.book_id}?${h.sheet === "sett" ? "sheet=sett&" : ""}start=${h.entry_date}&end=${h.entry_date}`)}>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono text-slate-500">{when(h.at).slice(11)}</span>
            <span className={`font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 text-[10px] ${ACTION_STYLE[h.action]}`}>{h.action}</span>
            <span className="font-bold uppercase">{h.book_name}</span>
            {h.sheet === "sett" && <span className="text-[10px] font-bold bg-amber-200 px-1.5">NEW SETT</span>}
            <span className="text-slate-500">entry of {ddmmyyyy(h.entry_date)}</span>
          </div>
          <ChangeList changes={h.changes} action={h.action} />
        </li>
      ))}
    </ul>
  </section>
);

export default function ActivityPage() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [day, setDay] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAdmin || !day) return;
    setData(null);
    api.get("/activity", { params: dayBounds(day) }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [day, isAdmin]);

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Admin · who changed what</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Activity</h1>
          </div>
          <div className="flex gap-2">
            <button data-testid="activity-today-btn" onClick={() => setDay(todayISO())}
              className="h-10 px-3 text-xs font-semibold border border-slate-300 bg-white hover:bg-slate-900 hover:text-white transition-colors">Today</button>
            <input type="date" data-testid="activity-date-input" value={day} max={todayISO()} onChange={(e) => setDay(e.target.value)}
              className="h-10 px-3 border border-slate-300 bg-white font-mono text-sm outline-none focus:border-slate-900" />
          </div>
        </div>
        {!data ? <p className="text-sm text-slate-500" data-testid="activity-loading">Loading…</p>
          : data.length === 0 ? (
            <div className="border border-dashed border-slate-400 bg-white/60 p-10 text-center" data-testid="activity-empty">
              <p className="font-semibold">No changes on {ddmmyyyy(day)}</p>
              <p className="text-sm text-slate-500 mt-1">Entries added, edited or deleted on this day will show here.</p>
            </div>
          ) : data.map((p) => <Person key={p.by_id} p={p} nav={nav} />)}
      </main>
    </div>
  );
}
